import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";

/**
 * /auth/verify-school?token=... — the target of every magic link email.
 * Marks the account as school-verified, refreshes AuthContext, and gives
 * the user a friendly "you're in" screen with a route back to whatever
 * they were doing.
 */
export default function VerifySchool() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", school: null, error: null });

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState({ status: "error", error: "Missing token." }); return; }
    api.confirmSchoolVerify(token)
      .then(async ({ data }) => {
        // Backend returns a fresh JWT — auto-log-the-user-in.
        try {
          if (data.token) {
            localStorage.setItem("hb_token", data.token);
          }
        } catch { /* ignore */ }
        try { await refresh?.(); } catch { /* ignore */ }
        setState({ status: "ok", school: data.school, error: null });
      })
      .catch((e) => setState({ status: "error", error: errMsg(e) }));
  }, [params, refresh]);

  const goBack = () => {
    try {
      const last = localStorage.getItem("hb_last_tryon_product_url");
      if (last) { localStorage.removeItem("hb_last_tryon_product_url"); navigate(last); return; }
    } catch { /* ignore */ }
    navigate("/try-on");
  };

  return (
    <div data-testid="verify-school-page" className="min-h-screen flex items-center justify-center text-center px-6 py-20"
         style={{ background: "radial-gradient(ellipse at top, #2a1a35 0%, #140b1c 80%)" }}>
      <div className="relative z-10 max-w-lg animate-fade-up">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-champagne mx-auto mb-8 animate-spin" strokeWidth={1.5} />
            <p className="font-label text-[11px] text-champagne tracking-widest mb-4">CONFIRMING YOUR SCHOOL EMAIL</p>
            <h1 className="font-display text-4xl text-ivory">One second…</h1>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-champagne/15 flex items-center justify-center" style={{ boxShadow: "0 0 60px rgba(247,231,206,0.3)" }}>
              <CheckCircle2 className="w-9 h-9 text-champagne" />
            </div>
            <p className="font-label text-[11px] text-champagne tracking-widest mb-5">SCHOOL VERIFIED</p>
            <h1 className="font-display text-5xl sm:text-6xl text-ivory mb-4">You're in.</h1>
            <p className="font-body text-sm text-ivory/60 mb-3 leading-relaxed">
              We just confirmed you at <b className="text-champagne">{state.school?.name || "your school"}</b>.
            </p>
            <p className="font-body text-sm text-ivory/60 mb-10 leading-relaxed flex items-center justify-center gap-1.5">
              <Sparkles className="w-4 h-4 text-champagne" /> Your first AI try-on is on us.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={goBack} data-testid="verify-back-to-flow" className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-sm hover:bg-ivory transition-colors">Back to the Dress</button>
              <Link to="/try-on" className="border border-ivory/30 text-ivory px-8 py-3.5 font-label text-[10px] rounded-sm hover:border-ivory transition-colors">Start Styling</Link>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-ivory/40 mx-auto mb-6" />
            <h1 className="font-display text-4xl text-ivory mb-3">Link expired</h1>
            <p className="font-body text-sm text-ivory/50 mb-8 max-w-md mx-auto leading-relaxed">
              {state.error || "This link isn't valid anymore. Request a fresh one and it'll be in your inbox in a few seconds."}
            </p>
            <Link to="/try-on" className="bg-champagne text-ink px-8 py-3.5 font-label text-[10px] rounded-sm">Try Again</Link>
          </>
        )}
      </div>
    </div>
  );
}
